import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useI18n } from '../hooks/useI18n';
import { ExternalLink, Calendar } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function SocialFeeds() {
  const { t } = useI18n();
  const [socialLinks, setSocialLinks] = useState([]);
  const [socialPosts, setSocialPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [settingsRes, postsRes] = await Promise.all([
        axios.get(`${API_URL}/api/site-settings`),
        axios.get(`${API_URL}/api/social-posts`)
      ]);
      
      const links = settingsRes.data?.footer?.section1?.social_links || [];
      setSocialLinks(links);
      setSocialPosts(postsRes.data || []);
    } catch (error) {
      console.error('Error fetching social data:', error);
    } finally {
      setLoading(false);
    }
  };

  const platformConfig = {
    facebook: {
      name: 'Facebook',
      color: 'bg-[#1877F2]',
      hoverColor: 'hover:bg-[#166FE5]',
      icon: '/icons/facebook.svg'
    },
    twitter: {
      name: 'X / Twitter',
      color: 'bg-[#1DA1F2]',
      hoverColor: 'hover:bg-[#1A91DA]',
      icon: '/icons/twitter.svg'
    },
    instagram: {
      name: 'Instagram',
      color: 'bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500',
      hoverColor: 'hover:opacity-90',
      icon: '/icons/instagram.svg'
    },
    youtube: {
      name: 'YouTube',
      color: 'bg-[#FF0000]',
      hoverColor: 'hover:bg-[#E60000]',
      icon: '/icons/youtube.png'
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const diffTime = Math.abs(now - date);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return `${diffDays} days ago`;
      if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
      
      return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  // Group posts by platform and get latest posts (sorted newest first)
  const getPostsForPlatform = (platform) => {
    return socialPosts
      .filter(post => post.platform === platform)
      .sort((a, b) => new Date(b.post_date) - new Date(a.post_date)) // Newest first
      .slice(0, 5); // Show up to 5 recent posts per platform
  };

  if (loading || socialLinks.length === 0) {
    return null;
  }

  return (
    <section className="py-8 md:py-14 bg-[#F4F8EF]" data-testid="social-feeds">
      <div className="container-cish">
        {/* Section heading */}
        <div className="section-heading">
          <img src="/icons/earth.svg" alt="" className="w-5 h-5 md:w-6 md:h-6" aria-hidden="true" />
          <h2 className="section-heading-text">{t('inSocialMedia')}</h2>
        </div>

        {/* Social media grid - 2 columns on mobile, 4 on desktop */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {socialLinks.map((link) => {
            const config = platformConfig[link.platform] || platformConfig.facebook;
            const platformPosts = getPostsForPlatform(link.platform);
            
            return (
              <div
                key={link.platform}
                className="bg-white rounded-xl border-2 border-gray-100 overflow-hidden transition-all duration-300 hover:shadow-xl hover:border-[#0E6D2B] flex flex-col"
                style={{ height: '450px' }}
              >
                {/* Platform Header */}
                <div className={`${config.color} ${config.hoverColor} p-4 transition-all flex-shrink-0`}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center backdrop-blur-sm">
                      <img 
                        src={config.icon} 
                        alt={config.name}
                        className="w-6 h-6 object-contain"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                    <div className="text-white flex-1">
                      <p className="font-bold text-sm">{config.name}</p>
                      <p className="text-xs text-white/80">@ICAR_CISH</p>
                    </div>
                  </div>
                </div>

                {/* Scrollable Posts Container */}
                {platformPosts.length > 0 ? (
                  <div className="flex-1 overflow-y-auto custom-scroll">
                    <div className="p-4 space-y-4">
                      {platformPosts.map((post, idx) => (
                        <div key={post.id} className={`${idx > 0 ? 'pt-4 border-t border-gray-100' : ''}`}>
                          {/* Post Date */}
                          <div className="flex items-center gap-2 mb-2">
                            <Calendar className="w-3 h-3 text-gray-400" />
                            <span className="text-xs text-gray-500">{formatDate(post.post_date)}</span>
                          </div>
                          
                          {/* Post Content */}
                          <p className="text-gray-700 text-sm leading-relaxed mb-2">
                            {post.content}
                          </p>
                          
                          {/* Post Image */}
                          {post.image_url && (
                            <img 
                              src={post.image_url} 
                              alt="Post" 
                              className="w-full h-32 object-cover rounded-lg mb-2"
                              onError={(e) => e.target.style.display = 'none'}
                            />
                          )}
                          
                          {/* View Post Link */}
                          {post.post_url && (
                            <a
                              href={post.post_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1"
                            >
                              View Original Post <ExternalLink className="w-3 h-3" />
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="p-5 flex-1 flex flex-col justify-between">
                    {/* No posts - Show CTA */}
                    <div className="text-center mb-4 flex-1 flex items-center justify-center">
                      <p className="text-gray-700 text-sm leading-relaxed">
                        Follow us on {config.name} for latest updates, research news, and insights from ICAR-CISH
                      </p>
                    </div>
                  </div>
                )}

                {/* Visit Profile Button (Footer) */}
                <div className="p-4 border-t border-gray-100 flex-shrink-0">
                  <a
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`${config.color} ${config.hoverColor} text-white text-xs font-semibold py-2 px-4 rounded-lg text-center transition-all flex items-center justify-center gap-1 w-full`}
                  >
                    Visit {config.name}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
